/**
 * useQualificationForm — logica del Form 2 (VSL Gate) estratta in custom hook.
 * ★ Round 2 qualità — Extract Hook da QualificationModal.jsx: il componente
 * resta solo JSX/orchestrazione; qui vivono hydration, salvataggio progressivo,
 * navigazione (con pixel tracking) e submit finale. Comportamento INVARIATO.
 */
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { api } from '../../lib/api';
import { logger } from '../../lib/logger';
import { toast } from 'sonner';
import {
  trackCompleteRegistration, trackForm2Start, trackForm2StepComplete,
} from '../../lib/pixel';
import {
  STEPS, STORAGE_LEAD_ID, STORAGE_PHASE1, STORAGE_QUALIFICA_DONE,
  STORAGE_FORM_DRAFT, STORAGE_ORGANIC_PREFILL, STORAGE_ORGANIC_LEAD_ID,
  INITIAL_FORM, budgetOptionsForPath, canContinueForStep, extractUtm,
} from './constants';

export function useQualificationForm({ open, ctaSource }) {
  const [step, setStep] = useState(0);
  const [config, setConfig] = useState(null);
  const [loadingCfg, setLoadingCfg] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);

  const draftLeadIdRef = useRef(null);
  const draftDebounceRef = useRef(null);
  // ★ Meta Pixel: dedupe `Form2StepStart` per modal open cycle.
  const startedRef = useRef(false);

  // ── HYDRATION ─────────────────────────────────────────────────────────
  // Reset on open + load config + prefill from Form 1 / draft / organic link.
  useEffect(() => {
    if (!open) {
      startedRef.current = false;
      return;
    }
    setStep(0);
    setError('');
    setResult(null);

    if (!startedRef.current) {
      startedRef.current = true;
      try { trackForm2Start({ source: ctaSource }); } catch (err) {
        logger.debug('QualificationModal: trackForm2Start failed', err);
      }
    }

    // 0) ORGANIC magic-link prefill (sessionStorage, highest priority)
    try {
      const organicRaw = sessionStorage.getItem(STORAGE_ORGANIC_PREFILL);
      const organicLeadId = sessionStorage.getItem(STORAGE_ORGANIC_LEAD_ID);
      if (organicRaw) {
        const o = JSON.parse(organicRaw);
        if (o && typeof o === 'object') {
          const fullName = (o.name || '').trim();
          const parts = fullName.split(/\s+/);
          const firstName = parts.shift() || '';
          const lastName = parts.join(' ');
          setForm((f) => ({
            ...f,
            first_name: firstName,
            last_name: lastName,
            email: o.email || '',
            phone: o.phone || '',
            phone_e164: o.phone || '',
          }));
        }
        if (organicLeadId) {
          draftLeadIdRef.current = organicLeadId;
          localStorage.setItem(STORAGE_LEAD_ID, organicLeadId);
        }
      }
    } catch (err) {
      logger.debug('QualificationModal: organic prefill hydration failed', err);
    }

    // 1) Live draft from previous session
    try {
      const draftRaw = localStorage.getItem(STORAGE_FORM_DRAFT);
      if (draftRaw) {
        const draft = JSON.parse(draftRaw);
        if (draft && typeof draft === 'object') {
          setForm((f) => ({ ...f, ...draft }));
        }
      }
    } catch (err) {
      logger.debug('QualificationModal: draft hydration failed', err);
    }

    // 2) VSL Gate Form 1 phase1 payload
    try {
      const phase1Raw = localStorage.getItem(STORAGE_PHASE1);
      if (phase1Raw && phase1Raw !== '1') {
        const p1 = JSON.parse(phase1Raw);
        if (p1 && typeof p1 === 'object') {
          setForm((f) => ({
            ...f,
            first_name: f.first_name || p1.first_name || '',
            last_name: f.last_name || p1.last_name || '',
            email: f.email || p1.email || '',
            phone: f.phone || p1.phone || '',
            phone_e164: f.phone_e164 || p1.phone_e164 || '',
            phone_country_code: f.phone_country_code || p1.phone_country_code || 'IT',
          }));
        }
      }
    } catch (err) {
      logger.debug('QualificationModal: phase1 hydration failed', err);
    }

    // 3) Backend lead snapshot (if we have a stored lead_id)
    try {
      const stored = localStorage.getItem(STORAGE_LEAD_ID);
      if (stored) {
        draftLeadIdRef.current = stored;
        api.get(`/public/qualifica/lead/${stored}`)
          .then((r) => {
            const d = r?.data || {};
            setForm((f) => ({
              first_name: f.first_name || d.first_name || '',
              last_name: f.last_name || d.last_name || '',
              email: f.email || d.email || '',
              phone: f.phone || d.phone || '',
              phone_e164: f.phone_e164 || d.phone_e164 || d.phone || '',
              phone_country_code: f.phone_country_code || d.phone_country_code || 'IT',
              countries: f.countries?.length ? f.countries : (d.countries || []),
              path: f.path || d.path || '',
              budget_bracket: f.budget_bracket || d.budget_bracket || '',
              timeline: f.timeline || d.timeline || '',
            }));
          })
          .catch(() => {});
      }
    } catch (err) {
      logger.debug('QualificationModal: backend lead hydration failed', err);
    }

    if (!config && !loadingCfg) {
      setLoadingCfg(true);
      api.get('/public/qualifica/config')
        .then((r) => setConfig(r.data))
        .catch(() => setError('Errore caricamento configurazione'))
        .finally(() => setLoadingCfg(false));
    }
  }, [open, config, loadingCfg, ctaSource]);

  // ── PROGRESSIVE SAVE ──────────────────────────────────────────────────
  // Debounce 600ms, then save draft + upsert to backend if email is valid.
  useEffect(() => {
    if (!open) return undefined;
    if (draftDebounceRef.current) clearTimeout(draftDebounceRef.current);
    draftDebounceRef.current = setTimeout(async () => {
      try {
        localStorage.setItem(STORAGE_FORM_DRAFT, JSON.stringify(form));
      } catch (err) {
        logger.debug('QualificationModal: localStorage draft save failed', err);
      }
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((form.email || '').trim());
      if (!emailOk) return;
      try {
        const utm = extractUtm();
        const r = await api.post('/public/qualifica/draft', {
          lead_id: draftLeadIdRef.current || undefined,
          first_name: form.first_name?.trim() || undefined,
          last_name: form.last_name?.trim() || undefined,
          email: form.email.trim(),
          phone: form.phone_e164 || form.phone || undefined,
          phone_e164: form.phone_e164 || undefined,
          phone_country_code: form.phone_country_code || undefined,
          countries: form.countries?.length ? form.countries : undefined,
          path: form.path || undefined,
          budget_bracket: form.budget_bracket || undefined,
          timeline: form.timeline || undefined,
          current_step: STEPS[step] || 'contact',
          cta_source: ctaSource,
          utm: Object.keys(utm).length ? utm : undefined,
        });
        if (r?.data?.lead_id) {
          draftLeadIdRef.current = r.data.lead_id;
          try { localStorage.setItem(STORAGE_LEAD_ID, r.data.lead_id); } catch (err) {
            logger.debug('QualificationModal: localStorage lead_id save failed', err);
          }
        }
      } catch (err) {
        logger.debug('QualificationModal progressive save best-effort failed', err);
      }
    }, 600);
    return () => {
      if (draftDebounceRef.current) clearTimeout(draftDebounceRef.current);
    };
  }, [form, step, open, ctaSource]);

  // ── NAVIGATION ────────────────────────────────────────────────────────
  const next = useCallback(() => setStep((s) => {
    const nextStep = Math.min(s + 1, STEPS.length - 1);
    if (nextStep !== s) {
      try {
        trackForm2StepComplete({
          step: STEPS[s] || `step_${s}`,
          total: STEPS.length - 1,
          source: ctaSource,
        });
      } catch (err) { logger.debug('trackForm2StepComplete failed', err); }
    }
    return nextStep;
  }), [ctaSource]);
  const back = useCallback(() => setStep((s) => Math.max(s - 1, 0)), []);

  const canContinue = useMemo(() => canContinueForStep(STEPS[step], form), [form, step]);
  const budgetOptions = useMemo(() => budgetOptionsForPath(config, form.path), [config, form.path]);

  const toggleCountry = useCallback((id) => {
    setForm((f) => ({
      ...f,
      countries: f.countries.includes(id)
        ? f.countries.filter((c) => c !== id)
        : [...f.countries, id],
    }));
  }, []);

  // ── SUBMIT ────────────────────────────────────────────────────────────
  const submit = useCallback(async () => {
    setError('');
    setSubmitting(true);
    try {
      const utm = extractUtm();
      let lead_id = draftLeadIdRef.current || null;
      try {
        if (!lead_id) lead_id = localStorage.getItem(STORAGE_LEAD_ID) || null;
      } catch (err) {
        logger.debug('QualificationModal: localStorage lead_id read failed', err);
      }

      const r = await api.post('/public/qualifica', {
        lead_id,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone_e164 || form.phone || undefined,
        phone_e164: form.phone_e164 || undefined,
        phone_country_code: form.phone_country_code || undefined,
        countries: form.countries,
        path: form.path,
        budget_bracket: form.budget_bracket,
        timeline: form.timeline,
        consent_privacy: true,
        cta_source: ctaSource,
        utm: Object.keys(utm).length ? utm : undefined,
      });
      setResult(r.data);
      try {
        localStorage.setItem(STORAGE_QUALIFICA_DONE, '1');
        if (r.data?.lead_id) localStorage.setItem(STORAGE_LEAD_ID, r.data.lead_id);
        localStorage.removeItem(STORAGE_FORM_DRAFT);
      } catch (err) {
        logger.debug('QualificationModal: localStorage post-submit cleanup failed', err);
      }
      try {
        trackCompleteRegistration({
          leadId: r?.data?.lead_id,
          path: form.path,
          country: (form.countries && form.countries[0]) || undefined,
          budget: form.budget_bracket,
        });
      } catch (err) {
        logger.debug('QualificationModal: pixel trackCompleteRegistration failed', err);
      }
      setStep(STEPS.indexOf('success'));
      toast.success('Profilo qualificato — ti scriviamo subito.');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Errore. Riprova tra poco.');
    } finally {
      setSubmitting(false);
    }
  }, [form, ctaSource]);

  return {
    step,
    currentStepKey: STEPS[step],
    config,
    loadingCfg,
    submitting,
    error,
    result,
    form,
    setForm,
    next,
    back,
    canContinue,
    budgetOptions,
    toggleCountry,
    submit,
  };
}
